<script setup lang="ts">
import { TriangleAlert } from '@lucide/vue'
import {
    Button,
    Input,
    Label,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { API_TOKEN_RESOURCES, type ApiTokenRow, api, formatAgo, type IssuedApiToken } from '@/api'
import CopyButton from '@/components/CopyButton.vue'
import DocsLink from '@/components/DocsLink.vue'
import ScopeLevelAll from '@/components/ScopeLevelAll.vue'
import ScopeLevelPicker from '@/components/ScopeLevelPicker.vue'
import type { ScopeLevel as Level } from '@/composables/useScopeLevels'

const tokens = ref<ApiTokenRow[]>([])
const error = ref<string | null>(null)
const loading = ref(true)
const busy = ref(false)
const name = ref('')
/** One level per base resource. Every child endpoint inherits whatever its resource is given. */
const levels = ref<Record<string, Level>>(Object.fromEntries(API_TOKEN_RESOURCES.map(r => [r, 'none'])))
/** The one moment a token exists in the portal. Cleared as soon as the person dismisses it. */
const issued = ref<IssuedApiToken | null>(null)
const now = ref(Date.now())

const scopes = computed(() =>
    API_TOKEN_RESOURCES.flatMap(resource =>
        levels.value[resource] === 'none' ? [] : [`${resource}:${levels.value[resource]}`],
    ),
)
const canCreate = computed(() => name.value.trim() !== '' && scopes.value.length > 0)

async function load(): Promise<void> {
    try {
        tokens.value = await api.apiTokens()
        error.value = null
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not load tokens (${caught.message}).` : 'Could not load tokens.'
    } finally {
        loading.value = false
    }
}

onMounted(load)

/** The empty state's one action: the New token form is always on the page, so take the person to its first field. */
function focusNewToken(): void {
    document.getElementById('token-name-field')?.focus()
}

async function act(run: () => Promise<IssuedApiToken | ApiTokenRow>, showSecret: boolean): Promise<void> {
    busy.value = true
    error.value = null
    try {
        const result = await run()
        if (showSecret && 'token' in result) {
            issued.value = result
        }
        await load()
    } catch (caught) {
        error.value = caught instanceof Error ? `That did not work (${caught.message}).` : 'That did not work.'
    } finally {
        busy.value = false
    }
}

async function create(): Promise<void> {
    await act(() => api.createApiToken({ name: name.value, scopes: scopes.value }), true)
    if (!error.value) {
        name.value = ''
        levels.value = Object.fromEntries(API_TOKEN_RESOURCES.map(r => [r, 'none']))
    }
}

/** The token whose scopes are being edited, and the levels chosen for it so far. One at a time. */
const editingId = ref<string | null>(null)
const editLevels = ref<Record<string, Level>>({})

const editScopes = computed(() =>
    API_TOKEN_RESOURCES.flatMap(resource =>
        (editLevels.value[resource] ?? 'none') === 'none' ? [] : [`${resource}:${editLevels.value[resource]}`],
    ),
)

function startEdit(row: ApiTokenRow): void {
    const held = new Map(row.scopes.map(scope => scope.split(':') as [string, Level]))
    editLevels.value = Object.fromEntries(API_TOKEN_RESOURCES.map(r => [r, held.get(r) ?? 'none']))
    editingId.value = row.id
}

async function saveEdit(row: ApiTokenRow): Promise<void> {
    await act(() => api.updateApiTokenScopes(row.id, editScopes.value), false)
    if (!error.value) {
        editingId.value = null
    }
}

function rotate(row: ApiTokenRow): void {
    void act(() => api.rotateApiToken(row.id), true)
}

function revoke(row: ApiTokenRow): void {
    if (!confirm(`Revoke "${row.name}"? Anything using it stops working immediately.`)) {
        return
    }
    void act(() => api.revokeApiToken(row.id), false)
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<!--
			The kit title and headings read their text from the slot: a `title` prop would be silently dropped.
			The `KeyRound` icon is dropped on purpose. The kit title draws no icon (it takes a kicker, the text
			and a meta line), and no migrated screen passes one. Before, `:icon` and `title` only landed as
			attributes on the header, so the screen showed an empty title and no icon.
		-->
		<ZhPageTitle>
			API tokens
			<template #meta><DocsLink path="/docs/api/authentication" /></template>
		</ZhPageTitle>
		<p class="text-muted-foreground max-w-2xl text-sm">
			A token authenticates a machine — a monitor, a cron, an agent — against this enterprise's API. Send it as
			<code>Authorization: Bearer &lt;token&gt;</code>. Scope it per resource: every child endpoint inherits its
			resource's level, so <code>runs:read</code> also reads a run's log, and <code>write</code> includes
			<code>read</code>. Tokens are created, rotated and revoked by a signed-in human only.
		</p>

		<p v-if="error" class="text-destructive text-sm" data-testid="error">{{ error }}</p>

		<!-- The one moment a secret is on screen, on a kit surface of its own so it cannot be read as part of the form. -->
		<ZhPanel v-if="issued" as="div" class="space-y-2" data-testid="issued-token">
			<p class="flex items-center gap-2 text-sm font-medium">
				<TriangleAlert class="size-4" />
				Copy this now — it is never shown again.
			</p>
			<div class="flex items-center gap-2">
				<code class="grow overflow-x-auto font-mono text-xs">{{ issued.token }}</code>
				<CopyButton :value="issued.token" />
			</div>
			<Button size="sm" variant="outline" data-testid="dismiss-token" @click="issued = null">Done</Button>
		</ZhPanel>

		<ZhPanel class="space-y-3" aria-labelledby="new-token">
			<!-- The All choice sets every resource at once; a resource changed after it leaves All reading "Mixed". -->
			<div class="flex flex-wrap items-center justify-between gap-3">
				<ZhSectionHeading id="new-token">New token</ZhSectionHeading>
				<ScopeLevelAll v-model="levels" testid="scope-all" credential />
			</div>
			<form class="space-y-4" data-testid="create-form" @submit.prevent="create">
				<!-- The field is the kit Input as the kit draws it; the width it keeps is set on its wrapper. -->
				<div class="max-w-64 space-y-2">
					<Label for="token-name-field">Name</Label>
					<Input
						id="token-name-field"
						v-model="name"
						required
						maxlength="80"
						placeholder="Monitoring"
						data-testid="token-name"
					/>
				</div>
				<ScopeLevelPicker v-model="levels" testid-prefix="scope-" credential />
				<Button type="submit" size="sm" data-testid="create-token" :disabled="busy || !canCreate">Create</Button>
			</form>
		</ZhPanel>

		<ZhPanel class="space-y-3" aria-labelledby="existing-tokens">
			<ZhSectionHeading id="existing-tokens">Tokens</ZhSectionHeading>
			<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
			<!-- Never had a token: say what will appear, and take the person to the New token form above. -->
			<ZhEmptyState v-else-if="tokens.length === 0" data-testid="no-tokens">
				No tokens yet. API tokens you create appear here, with their scopes and when each was last used.
				<template #action>
					<Button type="button" size="sm" @click="focusNewToken">Create a token</Button>
				</template>
			</ZhEmptyState>
			<Table v-else>
				<TableHeader>
					<TableRow>
						<TableHead>Name</TableHead>
						<TableHead>Token</TableHead>
						<TableHead>Scopes</TableHead>
						<TableHead>Last used</TableHead>
						<TableHead>Created</TableHead>
						<TableHead></TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<template v-for="row in tokens" :key="row.id">
						<TableRow :data-testid="`token-${row.id}`">
							<TableCell :class="row.revokedAt ? 'text-muted-foreground line-through' : ''">{{ row.name }}</TableCell>
							<TableCell><code class="font-mono text-xs">zhos_…{{ row.hint }}</code></TableCell>
							<TableCell class="font-mono text-xs whitespace-normal">{{ row.scopes.join(' ') }}</TableCell>
							<TableCell>{{ row.lastUsedAt ? formatAgo(now - Date.parse(row.lastUsedAt)) : 'never' }}</TableCell>
							<TableCell>{{ formatAgo(now - Date.parse(row.createdAt)) }}</TableCell>
							<TableCell class="text-right">
								<span v-if="row.revokedAt" class="text-muted-foreground text-xs">revoked</span>
								<span v-else class="flex justify-end gap-2">
									<Button
										size="sm"
										variant="outline"
										:data-testid="`edit-${row.id}`"
										:disabled="busy || editingId === row.id"
										@click="startEdit(row)"
									>
										Edit scopes
									</Button>
									<Button size="sm" variant="outline" :data-testid="`rotate-${row.id}`" :disabled="busy" @click="rotate(row)">
										Rotate
									</Button>
									<Button size="sm" variant="outline" :data-testid="`revoke-${row.id}`" :disabled="busy" @click="revoke(row)">
										Revoke
									</Button>
								</span>
							</TableCell>
						</TableRow>
						<!-- One cell across every column; prose wraps here, unlike the kit cell's single line. -->
						<TableRow v-if="editingId === row.id" :data-testid="`edit-panel-${row.id}`">
							<TableCell colspan="6" class="space-y-3 whitespace-normal">
								<p class="text-muted-foreground text-xs">
									Changing scopes keeps the same secret, so nothing using
									<code>zhos_…{{ row.hint }}</code> needs redeploying. It takes effect on the next request.
								</p>
								<ScopeLevelPicker v-model="editLevels" testid-prefix="edit-scope-" credential />
								<div class="flex gap-2">
									<Button
										size="sm"
										:data-testid="`save-scopes-${row.id}`"
										:disabled="busy || editScopes.length === 0"
										@click="saveEdit(row)"
									>
										Save scopes
									</Button>
									<Button size="sm" variant="outline" :data-testid="`cancel-edit-${row.id}`" @click="editingId = null">
										Cancel
									</Button>
								</div>
							</TableCell>
						</TableRow>
					</template>
				</TableBody>
			</Table>
		</ZhPanel>
	</section>
</template>
